import HugeiconsSwipeLeft08 from '@/components-v2/icons/HugeiconsSwipeLeft08';
import SolarAddCircleOutline from '@/components-v2/icons/SolarAddCircleOutline';
import SolarPenBold from '@/components-v2/icons/SolarPenBold';
import SolarPenNewSquareOutline from '@/components-v2/icons/SolarPenNewSquareOutline';
import SolarTrashBinTrashBold from '@/components-v2/icons/SolarTrashBinTrashBold';
import SolarUploadOutline from '@/components-v2/icons/SolarUploadOutline';
import { useToast } from '@/components/toast';
import { UPDATE_CANVAS_IMAGE_STATE_EVENT, UPDATE_PREVIEW_RATIO_EVENT, globalMitt } from '@/utils/global-mitt';
import { isMac } from '@/utils/helper';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslate } from '@tolgee/react';
import dynamic from 'next/dynamic';
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useDragAndDrop, useFileValidation, useOssUpload, usePasteHandler } from './hooks';
import {
  CompositionResult,
  ImageInfo,
  calculateOptimalComposition,
  renderCompositionToCanvas,
} from './image-composition-utils';
import type { DragDropFileUploadProps } from './index';
import { useFileHandler, useImagePreview, useUploadState } from './shared-hooks';
import { previewTextureBackground } from './texture-styles';
import { createEventHandler, normalizeImageAspectRatio } from './utils';

const CanvasPanel = dynamic(() => import('@/components/canvas-panel'), { ssr: false });

// 公共样式常量
const iconContainerStyle = (color: string, size = 36) => ({
  width: size,
  height: size,
  borderRadius: '50%',
  bgcolor: alpha(color, 0.1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: color.replace('.main', ''),
});

const textStyle = {
  p: 0,
  color: 'text.main',
  lineHeight: 1.4,
  fontSize: '0.8125rem',
  fontWeight: 400,
};

// 统一的分割线样式
const dividerStyle = {
  width: '100%',
  maxWidth: 240,
  height: 1.5,
  bgcolor: 'divider',
  borderRadius: 1,
  flexShrink: 0,
};

// 统一的功能区域样式
const sectionStyle = (theme: any, hoverColor: string) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  justifyContent: 'center',
  cursor: 'pointer',
  borderRadius: 4,
  p: 1.25,
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  '@media (hover: hover)': {
    '&:hover': {
      bgcolor: alpha(hoverColor, 0.075),
    },
  },
});

// Loading覆盖层样式
const loadingOverlayStyle = (theme: any) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(4px)',
  borderRadius: 2,
  gap: 1,
  zIndex: 999,
});

// 次要文本样式
const secondaryTextStyle = {
  color: 'text.secondary',
  textAlign: 'center',
  fontSize: '0.75rem',
  fontWeight: 500,
};

// 通用事件阻止函数
const stopEvent = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

// 额外上传区域内容样式
const additionalUploadContentStyle = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1.5,
  py: 1,
};

const CanvasModeComponent = forwardRef<HTMLDivElement, DragDropFileUploadProps>(
  (
    {
      onUploadComplete,
      onUploadError,
      size = 'medium',
      sx,
      initialAspectRatio = 1,
      minPixel = 0,
      maxPixel = Infinity,
      value,
      onChange,
      error,
      helperText,
    },
    ref,
  ) => {
    maxPixel = 100 * 100;
    const { t } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { showToast } = useToast();

    const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

    // 使用新的共享hooks
    const { containerRef, loading, setLoading } = useUploadState({ ref });
    const { imagePreview, aspectRatio, updatePreview, clearPreview } = useImagePreview({
      initialAspectRatio,
      value,
    });
    const { fileInputRef, handleTriggerFileInput, handleChange, handleUrlDrop } = useFileHandler({
      mode: 'canvas',
      minPixel,
      maxPixel,
      onUploadComplete,
      onUploadError,
      onChange,
    });

    // 需要的原有hooks
    const { uploadFile } = useOssUpload({ onUploadComplete, onUploadError });
    const { isValidImageFile } = useFileValidation({ minPixel, maxPixel });

    // 飞行动画触发 - 简化版本，无需 Context
    const handleMouseEnterDragHint = useCallback((event: React.MouseEvent) => {
      // 检测鼠标按下状态 - 如果有按钮被按下则不触发动画
      if (event.buttons > 0) return;

      // 清除之前的定时器
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }

      // 设置 1 秒延迟定时器
      hoverTimerRef.current = setTimeout(() => {
        // 通过全局 historyRef 获取第一个图片元素（只在桌面端有效）
        const firstImageElement = (window as any).historyRef?.current?.getFirstImageElement?.();
        const uploadElement = containerRef.current;

        if (firstImageElement && uploadElement) {
          window.dispatchEvent(
            new CustomEvent('triggerFlyingAnimation', {
              detail: {
                sourceElement: firstImageElement,
                targetElement: uploadElement,
              },
            }),
          );
        }
      }, 500);
    }, []);

    // 鼠标离开清理定时器
    const handleMouseLeaveDragHint = useCallback(() => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    }, []);

    // 组件卸载时清理定时器
    useEffect(() => {
      return () => {
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
        }
      };
    }, []);

    // Canvas模式的累积拼接状态管理 - 使用useRef确保状态持久化
    const currentResultRef = useRef<CompositionResult | null>(null);
    const [currentResult, setCurrentResult] = useState<CompositionResult | null>(null);

    // 多文件处理进度状态
    const [processingProgress, setProcessingProgress] = useState<{ current: number; total: number } | null>(null);

    // 🎯 Canvas编辑模态框状态
    const [editModalOpen, setEditModalOpen] = useState(false);

    // 🎯 拖拽处理 - 使用ref来避免依赖问题
    const processMultipleFilesRef = useRef<((files: File[]) => Promise<void>) | null>(null);

    // 同步ref和state
    const updateCurrentResult = (newResult: CompositionResult | null) => {
      currentResultRef.current = newResult;
      setCurrentResult(newResult);
    };

    // 获取当前结果（优先使用ref）
    const getCurrentResult = () => {
      return currentResultRef.current || currentResult;
    };

    // 🎯 处理编辑按钮点击
    const handleEditClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); // 🎯 关键修复：阻止事件冒泡，避免触发图片预览的点击事件
        if (currentResult?.url) {
          setEditModalOpen(true);
        }
      },
      [currentResult?.url],
    );

    // 🎯 处理空状态Canvas编辑按钮点击
    const handleEmptyCanvasClick = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setEditModalOpen(true);
    }, []);

    // 🎯 处理Canvas编辑保存
    const handleEditSave = useCallback(
      async (result: { url: string; width: number; height: number }) => {
        try {
          setLoading(true);

          // 将编辑后的dataURL转换为File对象并上传到OSS
          const response = await fetch(result.url);
          const blob = await response.blob();
          const file = new File([blob], `edited-${Date.now()}.png`, { type: 'image/png' });

          const uploadedUrl = await uploadFile(file, { width: result.width, height: result.height });

          // 更新当前结果，使用OSS URL
          const newResult: CompositionResult = {
            url: uploadedUrl,
            width: Math.round(result.width),
            height: Math.round(result.height),
            aspectRatio: result.width / result.height,
            arrangement: 'horizontal', // 编辑后的图片，使用默认排列方式
          };
          updateCurrentResult(newResult);

          // 更新图片预览为OSS URL
          updatePreview(uploadedUrl, newResult.width, newResult.height);
          setEditModalOpen(false);

          // 通知外部组件状态变化
          if (onChange) {
            onChange({
              url: uploadedUrl,
              width: Math.round(result.width),
              height: Math.round(result.height),
            });
          }
        } catch (error) {
          console.error('Failed to save edited image:', error);
          showToast(`编辑图片上传失败：${error instanceof Error ? error.message : '未知错误'}`, 'error');
          if (onUploadError && error instanceof Error) {
            onUploadError(error);
          }
        } finally {
          setLoading(false);
        }
      },
      [onUploadComplete, onChange, onUploadError, showToast],
    );

    // 将外部ref与内部containerRef连接
    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(containerRef.current);
        } else {
          ref.current = containerRef.current;
        }
      }
    }, [ref]);

    // 同步外部value到内部currentResult状态
    useEffect(() => {
      if (value?.url && value.width && value.height) {
        const newResult: CompositionResult = {
          url: value.url,
          width: value.width,
          height: value.height,
          arrangement: 'horizontal',
          aspectRatio: value.width / value.height,
        };
        updateCurrentResult(newResult);
      } else {
        updateCurrentResult(null);
      }
    }, [value]);

    // 🎯 移除重复的value监听，useImagePreview已经处理了value同步
    // 这避免了图片显示的闪烁问题

    // 将单个文件处理为拼接
    const processFileToComposition = async (file: File): Promise<void> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            const imageDataUrl = reader.result as string;
            const img = new Image();

            img.onload = async () => {
              try {
                const width = img.width;
                const height = img.height;

                // if (!validateImageDimensions(width, height)) {
                //   reject(new Error(`图片尺寸不符合要求: ${width}x${height}`));
                //   return;
                // }

                await handleCompositionAddition(imageDataUrl, width, height);
                resolve();
              } catch (error) {
                reject(error);
              }
            };

            img.onerror = () => {
              reject(new Error('图片加载失败'));
            };

            img.src = imageDataUrl;
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = () => {
          reject(new Error('文件读取失败'));
        };

        reader.readAsDataURL(file);
      });
    };

    // 累积拼接处理函数
    const handleCompositionAddition = async (imageDataUrl: string, width: number, height: number) => {
      const existingResult = getCurrentResult();

      try {
        const newImageInfo: ImageInfo = {
          url: imageDataUrl,
          width,
          height,
        };

        if (!existingResult) {
          // 第一张图片，检查并规范化比例 - 启用像素数控制
          const normalizedResult = await normalizeImageAspectRatio(imageDataUrl, width, height, true);

          const firstResult: CompositionResult = {
            url: normalizedResult.dataUrl,
            width: normalizedResult.width,
            height: normalizedResult.height,
            arrangement: 'horizontal',
            aspectRatio: normalizedResult.width / normalizedResult.height,
          };
          updateCurrentResult(firstResult);
          updatePreview(normalizedResult.dataUrl, normalizedResult.width, normalizedResult.height);

          // 通知有图片了
          globalMitt.emit(UPDATE_CANVAS_IMAGE_STATE_EVENT, true);
        } else {
          // 与当前结果进行拼接
          const existingImageInfo: ImageInfo = {
            url: existingResult.url,
            width: existingResult.width,
            height: existingResult.height,
          };

          // 计算最优拼接方式
          const composition = calculateOptimalComposition(existingImageInfo, newImageInfo);

          // 渲染拼接结果 - 启用像素数控制
          const composedUrl = await renderCompositionToCanvas(existingImageInfo, newImageInfo, composition, true);

          // 检查并规范化拼接结果的比例 - 启用像素数控制
          const normalizedComposition = await normalizeImageAspectRatio(
            composedUrl,
            composition.width,
            composition.height,
            true,
          );

          // 更新结果
          const newResult: CompositionResult = {
            ...composition,
            url: normalizedComposition.dataUrl,
            width: normalizedComposition.width,
            height: normalizedComposition.height,
            aspectRatio: normalizedComposition.width / normalizedComposition.height,
          };

          updateCurrentResult(newResult);
          updatePreview(normalizedComposition.dataUrl, normalizedComposition.width, normalizedComposition.height);
        }
      } catch (error) {
        showToast(`拼接图片失败：${error instanceof Error ? error.message : '未知错误'}`, 'error');
      }
    };

    // 上传拼接结果到 OSS
    const uploadCompositionResult = async (dataUrl: string, width: number, height: number) => {
      try {
        // 将 dataUrl 转换为 File 对象
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        const file = new File([blob], `composition-${Date.now()}.jpg`, { type: 'image/jpeg' });

        const uploadedUrl = await uploadFile(file, { width, height });

        // 更新当前结果状态，使用OSS URL替换dataURL
        const currentResult = getCurrentResult();
        if (currentResult) {
          updateCurrentResult({
            ...currentResult,
            url: uploadedUrl, // 使用OSS URL替换dataURL
          });
        }

        // 更新预览图片为OSS URL
        updatePreview(uploadedUrl);

        // 通知外部组件状态变化
        if (onChange) {
          onChange({
            url: uploadedUrl,
            width,
            height,
          });
        }
      } catch (_error) {
        // 错误已在hook中处理
      }
    };

    // 多文件连续拼接处理函数
    const processMultipleFiles = useCallback(
      async (files: File[]) => {
        setLoading(true);
        setProcessingProgress({ current: 0, total: files.length });

        try {
          let processedCount = 0;
          for (const file of files) {
            if (!isValidImageFile(file)) continue;
            await processFileToComposition(file);
            processedCount++;
            setProcessingProgress({ current: processedCount, total: files.length });
          }

          // 自动上传拼接结果
          if (processedCount > 0) {
            const result = getCurrentResult();
            if (result) {
              await uploadCompositionResult(result.url, result.width, result.height);
            }
          }
        } catch (error) {
          showToast(`批量处理图片失败：${error instanceof Error ? error.message : '未知错误'}`, 'error');
        } finally {
          setLoading(false);
          setProcessingProgress(null);
        }
      },
      [
        setLoading,
        setProcessingProgress,
        isValidImageFile,
        processFileToComposition,
        getCurrentResult,
        uploadCompositionResult,
        showToast,
      ],
    );

    // 🎯 设置ref引用
    useEffect(() => {
      processMultipleFilesRef.current = processMultipleFiles;
    }, [processMultipleFiles]);

    const handleFileDrop = useCallback(async (files: File[]) => {
      if (processMultipleFilesRef.current) {
        await processMultipleFilesRef.current(files);
      }
    }, []);

    const { dragOver, handleDragOver, handleDragLeave, handleDrop } = useDragAndDrop({
      onFileDrop: handleFileDrop,
      onUrlDrop: handleUrlDrop,
      multiple: true,
    });

    // 粘贴处理
    const { handlePaste } = usePasteHandler({
      onFilePaste: (file) => {
        handleFileDrop([file]);
      },
    });

    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const result = handleChange(event);
        if (result?.type === 'single') {
          handleFileDrop([result.file]);
        } else if (result?.type === 'multiple') {
          handleFileDrop(result.files);
        }
      },
      [handleChange, handleFileDrop],
    );

    // 添加 paste 事件监听器
    useEffect(() => {
      const handlePasteEvent = (event: ClipboardEvent) => {
        handlePaste(event);
      };

      // 监听全局 paste 事件
      document.addEventListener('paste', handlePasteEvent);

      return () => {
        document.removeEventListener('paste', handlePasteEvent);
      };
    }, [handlePaste]);

    // 处理清除图片
    const handleClearImage = createEventHandler(() => {
      // Canvas模式：清空累积拼接状态
      updateCurrentResult(null);
      clearPreview();

      // 重置预览状态
      setTimeout(() => {
        globalMitt.emit(UPDATE_CANVAS_IMAGE_STATE_EVENT, false);
        globalMitt.emit(UPDATE_PREVIEW_RATIO_EVENT, '1:1');
      }, 0);

      // 通知外部值已清空
      if (onChange) {
        onChange({
          url: '',
          width: 0,
          height: 0,
        });
      }
    });

    const paddingTop = aspectRatio < 1 ? '100%' : `${(1 / aspectRatio) * 100}%`;

    return (
      <>
        <Box
          ref={containerRef}
          data-upload-component="true"
          data-canvas-mode="true"
          sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, position: 'relative', ...sx }}
        >
          {/* 主图片显示区域 */}
          <Paper
            variant="outlined"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
              borderRadius: 2,
              padding: size === 'small' ? 0.25 : 1,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: dragOver ? alpha(theme.palette.primary.main, 0.08) : 'background.neutral',
              position: 'relative',
              zIndex: 2,
              boxShadow: imagePreview ? `0 2px 8px ${alpha(theme.palette.grey[500], 0.12)}` : 'none',
              transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow'], {
                duration: theme.transitions.duration.shorter,
              }),
            }}
          >
            <input
              ref={fileInputRef}
              accept="image/jpeg,image/png,image/webp,image/bmp"
              style={{ display: 'none' }}
              id="raised-button-file"
              multiple
              type="file"
              onChange={handleInputChange}
            />
            <label htmlFor="raised-button-file" style={{ display: 'block', cursor: 'pointer' }}>
              {imagePreview ? (
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: 'transparent',
                    boxShadow: `0 0 0 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                    paddingTop,
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      ...previewTextureBackground,
                      pointerEvents: 'none',
                      zIndex: 0,
                    },
                  }}
                  className="preview-container"
                >
                  {/* Preview Image */}
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      borderRadius: 'inherit',
                      zIndex: 1,
                    }}
                    onClick={(e) => {
                      stopEvent(e);
                      handleTriggerFileInput(e);
                    }}
                    onError={(_e) => {
                      // 可以在这里显示一个错误提示或设置一个默认图片
                    }}
                  />

                  {/* Canvas模式：编辑和删除按钮 */}
                  <IconButton
                    onClick={handleEditClick}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 48,
                      width: 32,
                      height: 32,
                      bgcolor: alpha(theme.palette.primary.main, 0.9),
                      color: 'common.white',
                      '@media (hover: hover)': {
                        '&:hover': {
                          bgcolor: theme.palette.primary.main,
                          transform: 'scale(1.1)',
                        },
                      },
                      transition: theme.transitions.create(['background-color', 'transform'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      zIndex: 10,
                    }}
                    title="Edit with Canvas"
                  >
                    <SolarPenBold height={16} width={16} />
                  </IconButton>

                  <IconButton
                    onClick={handleClearImage}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      width: 32,
                      height: 32,
                      bgcolor: alpha(theme.palette.error.main, 0.9),
                      color: 'common.white',
                      '@media (hover: hover)': {
                        '&:hover': {
                          bgcolor: theme.palette.error.main,
                          transform: 'scale(1.1)',
                        },
                      },
                      transition: theme.transitions.create(['background-color', 'transform'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      zIndex: 10,
                    }}
                    title="清空所有图片"
                  >
                    <SolarTrashBinTrashBold height={16} width={16} />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {/* 上部分：上传图片 - primary配色 */}
                  <Box component="span" sx={sectionStyle(theme, theme.palette.primary.main)}>
                    <Box sx={iconContainerStyle(theme.palette.primary.main)}>
                      <SolarUploadOutline height={18} width={18} />
                    </Box>
                    {size === 'medium' && (
                      <Typography variant="body2" sx={textStyle}>
                        {t('image.hint.canvas_upload_click')}
                      </Typography>
                    )}
                  </Box>

                  {/* 中间部分：拖拽提示 - success配色，在移动端隐藏 */}
                  {!isMobile && size === 'medium' && (
                    <>
                      <Box sx={dividerStyle} />
                      <Box
                        sx={sectionStyle(theme, theme.palette.success.main)}
                        onMouseEnter={handleMouseEnterDragHint}
                        onMouseLeave={handleMouseLeaveDragHint}
                      >
                        <Box sx={iconContainerStyle(theme.palette.success.main)}>
                          <HugeiconsSwipeLeft08 height={18} width={18} />
                        </Box>
                        <Typography variant="body2" sx={textStyle}>
                          {t('image.hint.canvas_upload_drag', {
                            shortcutKey: isMac() ? ' ⌘ + v ' : ' Ctrl + v ',
                          })}
                        </Typography>
                      </Box>
                      <Box sx={dividerStyle} />
                    </>
                  )}

                  {/* 分隔线 - 仅在移动端显示 */}
                  {isMobile && <Box sx={dividerStyle} />}

                  {/* 下部分：Canvas编辑 - secondary配色 */}
                  <Box onClick={handleEmptyCanvasClick} sx={sectionStyle(theme, theme.palette.secondary.main)}>
                    <Box sx={iconContainerStyle(theme.palette.secondary.main)}>
                      <SolarPenNewSquareOutline height={18} width={18} />
                    </Box>
                    <Typography variant="body2" sx={textStyle}>
                      {t('image.hint.start_from_blank_canvas')}
                    </Typography>
                  </Box>
                </Box>
              )}
            </label>
            {error && (
              <Typography variant="caption" color="error">
                {helperText}
              </Typography>
            )}
            {loading && (
              <Box sx={loadingOverlayStyle(theme)}>
                <CircularProgress
                  size={24}
                  sx={{
                    color: 'primary.main',
                  }}
                />
                {processingProgress && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      textAlign: 'center',
                    }}
                  >
                    {`${processingProgress.current}/${processingProgress.total}`}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>

          {/* Canvas模式：卡包式重叠的额外上传区域 */}
          {imagePreview && (
            <Box sx={{ mt: -1.5, position: 'relative', zIndex: 1 }}>
              <Paper
                variant="outlined"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  borderLeft: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
                  borderRight: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
                  borderBottom: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
                  borderTop: 'none',
                  borderRadius: 2,
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  padding: 1,
                  paddingTop: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: dragOver
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: 'blur(8px)',
                  position: 'relative',
                  boxShadow: `0 -2px 8px ${alpha(theme.palette.grey[500], 0.12)}`,
                  transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                  '@media (hover: hover)': {
                    '&:hover': {
                      boxShadow: `0 -4px 12px ${alpha(theme.palette.grey[500], 0.16)}`,
                      bgcolor: dragOver
                        ? alpha(theme.palette.primary.main, 0.12)
                        : alpha(theme.palette.background.paper, 0.9),
                      borderLeft: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
                      borderRight: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
                      borderBottom: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
                      borderTop: 'none',
                    },
                  },
                }}
              >
                <label htmlFor="raised-button-file" style={{ display: 'block', cursor: 'pointer' }}>
                  <Box sx={additionalUploadContentStyle}>
                    <IconButton
                      color="primary"
                      aria-label="Add more images"
                      component="span"
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        '@media (hover: hover)': {
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.16),
                          },
                        },
                      }}
                    >
                      <SolarAddCircleOutline height={14} width={14} />
                    </IconButton>
                    <Typography variant="body2" sx={secondaryTextStyle}>
                      {t('image.hint.add_more_images')}
                    </Typography>
                  </Box>
                </label>
              </Paper>
            </Box>
          )}
        </Box>

        {/* Canvas模式：Canvas面板 - 条件渲染 */}
        {editModalOpen && (
          <CanvasPanel
            open
            onClose={() => setEditModalOpen(false)}
            initialImage={
              currentResult
                ? {
                    url: currentResult.url,
                    width: currentResult.width,
                    height: currentResult.height,
                  }
                : undefined
            }
            onSave={handleEditSave}
          />
        )}
      </>
    );
  },
);

CanvasModeComponent.displayName = 'CanvasModeComponent';

// 导出Canvas模式组件
export { CanvasModeComponent };
